import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import React from 'react';
import { Fighter } from '../../../../../../corelogic/domain/models/fighter.model';
import { FighterInfo } from '../../../../../../corelogic/domain/models/fighterInfo.model';
import { SeasonFights } from '../../../../../../corelogic/domain/models/seasonFights.model';
import { capitalizeFirstLetter } from '../../../../../../corelogic/domain/utils/capitalize.utils';
import { getWeightDisplayName } from '../../../../../../corelogic/domain/utils/weightUtils';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';
import ReusableAlert from '../../Shared/ReusableAlert/ReusableAlert';
import FighterPickerButton from './FighterPickerButton/FighterPickerButton';

interface FighterPickerProps {
  seasonFights: SeasonFights[];
  selectedCardId: number | null;
  fighters: Fighter[];
  fighterInfoArray: FighterInfo[];
  onFighterButtonClick: (
    fighterId: string,
    fightId: string,
    fightDate: Date,
    position?: number,
  ) => void;
}

const FighterPicker: React.FC<FighterPickerProps> = ({
  seasonFights,
  selectedCardId,
  fighters,
  onFighterButtonClick,
  fighterInfoArray,
}) => {
  const [openSnackbar, setOpenSnackbar] = React.useState(false);

  // Helper function to find the first empty position
  const findFirstEmptyCard = (): number | null => {
    if (!fighterInfoArray) return null;

    for (let i = 0; i < fighterInfoArray.length; i++) {
      if (
        fighterInfoArray[i].FightId === 'default' ||
        !fighterInfoArray[i].FightId
      ) {
        return i + 1;
      }
    }
    return null;
  };

  const handleButtonClick = () => {
    if (selectedCardId === null) {
      // Check if there's an empty position
      const firstEmptyCard = findFirstEmptyCard();

      if (firstEmptyCard !== null) {
        // If there's an empty position, we'll use it
        return firstEmptyCard;
      } else {
        // If all positions are filled, show the error message
        setOpenSnackbar(true);
        return null;
      }
    }
    return selectedCardId;
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // Function to find fighter information and will be used for Tooltip
  const getFighterInfo = (fighterId: string) => {
    const fighter = fighters.find((f) => f.id === fighterId);
    if (fighter) {
      return (
        <div
          style={{ fontSize: 'clamp(0.5rem, 0.8vw, 1rem)', lineHeight: '1.5' }}
        >
          <div>
            <strong>Gender:</strong> {capitalizeFirstLetter(fighter.gender)}
          </div>
          <div>
            <strong>Division:</strong> {getWeightDisplayName(fighter.weight)}
          </div>
          <div>
            <strong>Record:</strong> {fighter.wins}-{fighter.losses}-
            {fighter.draws}
          </div>
        </div>
      );
    }
    return 'Info not available';
  };

  // Helper function to categorize events
  const categorizeEvent = (eventDate: Date) => {
    const today = new Date();
    const eventDateOnly = new Date(
      eventDate.getFullYear(),
      eventDate.getMonth(),
      eventDate.getDate(),
    );
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    if (eventDateOnly > todayDateOnly) {
      return 'upcoming';
    } else if (eventDateOnly.getTime() === todayDateOnly.getTime()) {
      return 'in-progress';
    } else {
      return 'completed';
    }
  };

  // Helper function to sort events by date (earliest first)
  const sortEventsByDate = (
    events: Array<{ eventName: string; eventFights: any[]; category: string }>,
  ) => {
    return events.sort((a, b) => {
      const dateA = new Date(a.eventFights[0].date);
      const dateB = new Date(b.eventFights[0].date);
      return dateA.getTime() - dateB.getTime();
    });
  };

  const EventDivider = ({ title }: { title: string }) => (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        margin:
          title === 'COMPLETED EVENTS' ? '30px 0 20px 0' : '10px 0 20px 0',
      }}
    >
      <Box
        sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.3)' }}
      />
      <Typography
        sx={{
          color: 'white',
          fontFamily: 'Bebas Neue',
          fontSize: 'clamp(0.7rem, 1.1vw, 1.3rem)',
          whiteSpace: 'nowrap',
          letterSpacing: '1px',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-4px',
            left: '0',
            width: '100%',
            height: '2px',
            backgroundColor: '#cc0000',
          },
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.3)' }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'rgba(25, 25, 25, 0.7)',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '15px 20px',
          width: '100%',
          bgcolor: '#8B0000',
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Rubik Mono One',
            textAlign: 'center',
            color: 'white',
            fontSize: 'clamp(1rem, 1.6vw, 2rem)',
          }}
        >
          Fighter{' '}
          <Box
            component="span"
            role="img"
            aria-label="crossed swords"
            sx={{
              margin: '0 8px',
              display: 'inline-block',
              ...(selectedCardId && {
                '@keyframes throb-swords': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    color: 'inherit',
                  },
                  '50%': {
                    transform: 'scale(1.2)',
                    color: '#cc0000',
                  },
                },
                animation: 'throb-swords 1.5s ease-in-out infinite',
              }),
            }}
          >
            ⚔️
          </Box>{' '}
          Picker
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          },
        }}
      >
        {seasonFights.length > 0 ? (
          (() => {
            // Categorize and sort all events
            const categorizedEvents = Array.from(
              new Set(seasonFights.map((fight) => fight.event_name)),
            ).map((eventName) => {
              const eventFights = seasonFights.filter(
                (fight) => fight.event_name === eventName,
              );
              const category = categorizeEvent(new Date(eventFights[0].date));
              return { eventName, eventFights, category };
            });

            const upcomingEvents = sortEventsByDate(
              categorizedEvents.filter(
                ({ category }) => category === 'upcoming',
              ),
            );
            const inProgressEvents = sortEventsByDate(
              categorizedEvents.filter(
                ({ category }) => category === 'in-progress',
              ),
            );
            const completedEvents = sortEventsByDate(
              categorizedEvents.filter(
                ({ category }) => category === 'completed',
              ),
            );

            return (
              <>
                {/* UPCOMING EVENTS */}
                {upcomingEvents.length > 0 && (
                  <>
                    <EventDivider title="UPCOMING EVENTS" />
                    {upcomingEvents.map(({ eventName, eventFights }) => (
                      <Accordion
                        key={`section-${eventName}`}
                        sx={{
                          width: '100%',
                          bgcolor: 'rgba(20, 20, 20, 0.9)',
                          borderRadius: '4px',
                          marginBottom: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          '&.Mui-expanded': {
                            margin: 0,
                            marginBottom: '12px',
                          },
                          ...(selectedCardId && {
                            '@keyframes throb-accordion': {
                              '0%, 100%': {
                                borderColor: 'rgba(255, 255, 255, 0.08)',
                              },
                              '50%': {
                                borderColor: 'rgba(255, 255, 255, 0.8)',
                              },
                            },
                            animation:
                              'throb-accordion 1.5s ease-in-out infinite',
                          }),
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            bgcolor: 'rgba(40, 40, 40, 0.5)',
                            borderRadius: '4px',
                            minHeight: '40px',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                              bgcolor: 'rgba(60, 60, 60, 0.6)',
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '4px 0',
                            },
                            '&.Mui-expanded': {
                              borderBottom:
                                '1px solid rgba(255, 255, 255, 0.08)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '20px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <PulsingActiveIcon />
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Bebas Neue',
                                fontSize: {
                                  xs: '1.1rem',
                                  sm: 'clamp(0.9rem, 1.4vw, 1.5rem)',
                                },
                                lineHeight: 1.2,
                                color: 'rgba(255, 255, 255, 0.9)',
                              }}
                            >
                              {eventName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: '0.9rem',
                                  sm: 'clamp(0.8rem, 1vw, 1rem)',
                                },
                                lineHeight: 1,
                              }}
                            >
                              {new Date(
                                eventFights[0].date,
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: '8px',
                            backgroundColor: 'rgba(30, 30, 30, 0.4)',
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          <List>
                            {eventFights
                              .sort((a, b) => (a.title_fight === 'T' ? -1 : 1))
                              .map((fight) => (
                                <ListItem
                                  key={fight.fight_id}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Box sx={{ width: '45%' }}>
                                    <FighterPickerButton
                                      fighterName={fight.fighter_1}
                                      isTitleFight={fight.title_fight === 'T'}
                                      tooltipContent={getFighterInfo(
                                        fight.fighter_1_id,
                                      )}
                                      onClick={() => {
                                        if (selectedCardId !== null) {
                                          // if a card is already selected, use it
                                          onFighterButtonClick(
                                            fight.fighter_1_id,
                                            fight.fight_id,
                                            fight.date,
                                          );
                                        } else {
                                          // Otherwise, find an empty position
                                          const cardIdToUse =
                                            handleButtonClick();
                                          if (cardIdToUse) {
                                            onFighterButtonClick(
                                              fight.fighter_1_id,
                                              fight.fight_id,
                                              fight.date,
                                              cardIdToUse,
                                            );
                                          }
                                        }
                                      }}
                                      isEnabled={true}
                                      isUpcoming={true}
                                    />
                                  </Box>
                                  <Typography
                                    sx={{
                                      margin: '0 8px',
                                      fontFamily: 'Bebas Neue',
                                      fontSize: {
                                        xs: '1.1rem',
                                        sm: 'clamp(0.7rem, 0.7vw, 1rem)',
                                      },
                                    }}
                                  >
                                    vs
                                  </Typography>
                                  <Box sx={{ width: '45%' }}>
                                    <FighterPickerButton
                                      fighterName={fight.fighter_2}
                                      isTitleFight={fight.title_fight === 'T'}
                                      tooltipContent={getFighterInfo(
                                        fight.fighter_2_id,
                                      )}
                                      onClick={() => {
                                        if (selectedCardId !== null) {
                                          // if a card is already selected, use it
                                          onFighterButtonClick(
                                            fight.fighter_2_id,
                                            fight.fight_id,
                                            fight.date,
                                          );
                                        } else {
                                          // Otherwise, find an empty position
                                          const cardIdToUse =
                                            handleButtonClick();
                                          if (cardIdToUse) {
                                            onFighterButtonClick(
                                              fight.fighter_2_id,
                                              fight.fight_id,
                                              fight.date,
                                              cardIdToUse,
                                            );
                                          }
                                        }
                                      }}
                                      isEnabled={true}
                                      isUpcoming={true}
                                    />
                                  </Box>
                                </ListItem>
                              ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                )}

                {/* IN-PROGRESS EVENTS */}
                {inProgressEvents.length > 0 && (
                  <>
                    <EventDivider title="IN-PROGRESS EVENTS" />
                    {inProgressEvents.map(({ eventName, eventFights }) => (
                      <Accordion
                        key={`section-${eventName}`}
                        sx={{
                          width: '100%',
                          bgcolor: 'rgba(20, 20, 20, 0.9)',
                          borderRadius: '4px',
                          marginBottom: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          '&.Mui-expanded': {
                            margin: 0,
                            marginBottom: '12px',
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            bgcolor: 'rgba(40, 40, 40, 0.5)',
                            borderRadius: '4px',
                            minHeight: '40px',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                              bgcolor: 'rgba(60, 60, 60, 0.6)',
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '4px 0',
                            },
                            '&.Mui-expanded': {
                              borderBottom:
                                '1px solid rgba(255, 255, 255, 0.08)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '20px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <PulsingActiveIcon />
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Bebas Neue',
                                fontSize: {
                                  xs: '1.1rem',
                                  sm: 'clamp(0.9rem, 1.4vw, 1.5rem)',
                                },
                                lineHeight: 1.2,
                                color: 'rgba(255, 255, 255, 0.9)',
                              }}
                            >
                              {eventName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: '0.9rem',
                                  sm: 'clamp(0.8rem, 1vw, 1rem)',
                                },
                                lineHeight: 1,
                              }}
                            >
                              {new Date(
                                eventFights[0].date,
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: '8px',
                            backgroundColor: 'rgba(30, 30, 30, 0.4)',
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          <List>
                            {eventFights
                              .sort((a, b) => (a.title_fight === 'T' ? -1 : 1))
                              .map((fight) => (
                                <ListItem
                                  key={fight.fight_id}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Box sx={{ width: '45%' }}>
                                    <FighterPickerButton
                                      fighterName={fight.fighter_1}
                                      isTitleFight={fight.title_fight === 'T'}
                                      tooltipContent={getFighterInfo(
                                        fight.fighter_1_id,
                                      )}
                                      onClick={() => {
                                        if (selectedCardId !== null) {
                                          // if a card is already selected, use it
                                          onFighterButtonClick(
                                            fight.fighter_1_id,
                                            fight.fight_id,
                                            fight.date,
                                          );
                                        } else {
                                          // Otherwise, find an empty position
                                          const cardIdToUse =
                                            handleButtonClick();
                                          if (cardIdToUse) {
                                            onFighterButtonClick(
                                              fight.fighter_1_id,
                                              fight.fight_id,
                                              fight.date,
                                              cardIdToUse,
                                            );
                                          }
                                        }
                                      }}
                                      isEnabled={true}
                                      isUpcoming={true}
                                    />
                                  </Box>
                                  <Typography
                                    sx={{
                                      margin: '0 8px',
                                      fontFamily: 'Bebas Neue',
                                      fontSize: {
                                        xs: '1.3rem',
                                        sm: 'clamp(0.7rem, 0.7vw, 1rem)',
                                      },
                                    }}
                                  >
                                    vs
                                  </Typography>
                                  <Box sx={{ width: '45%' }}>
                                    <FighterPickerButton
                                      fighterName={fight.fighter_2}
                                      isTitleFight={fight.title_fight === 'T'}
                                      tooltipContent={getFighterInfo(
                                        fight.fighter_2_id,
                                      )}
                                      onClick={() => {
                                        if (selectedCardId !== null) {
                                          // if a card is already selected, use it
                                          onFighterButtonClick(
                                            fight.fighter_2_id,
                                            fight.fight_id,
                                            fight.date,
                                          );
                                        } else {
                                          // Otherwise, find an empty position
                                          const cardIdToUse =
                                            handleButtonClick();
                                          if (cardIdToUse) {
                                            onFighterButtonClick(
                                              fight.fighter_2_id,
                                              fight.fight_id,
                                              fight.date,
                                              cardIdToUse,
                                            );
                                          }
                                        }
                                      }}
                                      isEnabled={selectedCardId !== null}
                                      isUpcoming={false}
                                    />
                                  </Box>
                                </ListItem>
                              ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                )}

                {/* COMPLETED EVENTS */}
                {completedEvents.length > 0 && (
                  <>
                    <EventDivider title="COMPLETED EVENTS" />
                    {completedEvents.map(({ eventName, eventFights }) => (
                      <Accordion
                        key={`section-${eventName}`}
                        sx={{
                          width: '100%',
                          bgcolor: 'rgba(20, 20, 20, 0.9)',
                          borderRadius: '4px',
                          marginBottom: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          '&.Mui-expanded': {
                            margin: 0,
                            marginBottom: '12px',
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            bgcolor: 'rgba(40, 40, 40, 0.5)',
                            borderRadius: '4px',
                            minHeight: '40px',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                              bgcolor: 'rgba(60, 60, 60, 0.6)',
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '4px 0',
                            },
                            '&.Mui-expanded': {
                              borderBottom:
                                '1px solid rgba(255, 255, 255, 0.08)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Bebas Neue',
                                fontSize: {
                                  xs: '1.3rem',
                                  sm: 'clamp(0.9rem, 1.4vw, 1.5rem)',
                                },
                                lineHeight: 1.2,
                                color: 'rgba(255, 255, 255, 0.9)',
                              }}
                            >
                              {eventName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: '1.1rem',
                                  sm: 'clamp(0.8rem, 1vw, 1rem)',
                                },
                                lineHeight: 1,
                              }}
                            >
                              {new Date(
                                eventFights[0].date,
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: '8px',
                            backgroundColor: 'rgba(30, 30, 30, 0.4)',
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          <List>
                            {eventFights
                              .sort((a, b) => (a.title_fight === 'T' ? -1 : 1))
                              .map((fight) => (
                                <ListItem
                                  key={fight.fight_id}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Box sx={{ width: '45%' }}>
                                    <FighterPickerButton
                                      fighterName={fight.fighter_1}
                                      isTitleFight={fight.title_fight === 'T'}
                                      tooltipContent={getFighterInfo(
                                        fight.fighter_1_id,
                                      )}
                                      onClick={() => {
                                        handleButtonClick();
                                        if (selectedCardId !== null) {
                                          onFighterButtonClick(
                                            fight.fighter_1_id,
                                            fight.fight_id,
                                            fight.date,
                                          );
                                        }
                                      }}
                                      isEnabled={selectedCardId !== null}
                                      isUpcoming={false}
                                    />
                                  </Box>
                                  <Typography
                                    sx={{
                                      margin: '0 8px',
                                      fontFamily: 'Bebas Neue',
                                      fontSize: {
                                        xs: '1.3rem',
                                        sm: 'clamp(0.7rem, 0.7vw, 1rem)',
                                      },
                                    }}
                                  >
                                    vs
                                  </Typography>
                                  <Box sx={{ width: '45%' }}>
                                    <FighterPickerButton
                                      fighterName={fight.fighter_2}
                                      isTitleFight={fight.title_fight === 'T'}
                                      tooltipContent={getFighterInfo(
                                        fight.fighter_2_id,
                                      )}
                                      onClick={() => {
                                        handleButtonClick();
                                        if (selectedCardId !== null) {
                                          onFighterButtonClick(
                                            fight.fighter_2_id,
                                            fight.fight_id,
                                            fight.date,
                                          );
                                        }
                                      }}
                                      isEnabled={selectedCardId !== null}
                                      isUpcoming={false}
                                    />
                                  </Box>
                                </ListItem>
                              ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                )}
              </>
            );
          })()
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Bebas Neue',
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              fontSize: 'clamp(0.9rem, 1.5vw, 1.6rem)',
              padding: '20px',
              letterSpacing: '1px',
              borderBottom: '2px solid #cc0000',
              display: 'inline-block',
              marginBottom: '20px',
            }}
          >
            Select a season to get started!
          </Typography>
        )}
      </Box>
      <ReusableAlert
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        severity="warning"
        text="Please pick a fighter in your team to replace!"
      />
    </Box>
  );
};

export default FighterPicker;
